import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// An architectural, top-down line-art "Property Profile". Thin gold strokes, square joins,
// no fills except a very subtle active wash. Zones toggle in based on the user's profile,
// the active area is highlighted, and micro-areas (What Ascendia Sees) appear as small markers.
const GOLD = "rgba(223,200,120,";
const LINE = GOLD + "0.28)";
const LINE_ACTIVE = GOLD + "0.60)";
const WASH = "rgba(200,161,74,0.06)";

// Zone rectangles in a shared 400x300 viewBox.
const ZONES = {
  kitchen: { x: 24, y: 24, w: 150, h: 104, label: "Kitchen" },
  bathrooms: { x: 24, y: 140, w: 90, h: 88, label: "Bath" },
  bedrooms: { x: 186, y: 24, w: 190, h: 130, label: "Living / Bed" },
  floors: { x: 126, y: 140, w: 130, h: 136, label: "Hall" },
  balcony: { x: 268, y: 166, w: 108, h: 110, label: "Balcony" },
};

// Micro-hotspot points per area (approx positions on the plan).
const HOTSPOTS = {
  kitchen: [[36, 36], [162, 36], [36, 116]],
  bathrooms: [[36, 152], [102, 216], [60, 216]],
  bedrooms: [[198, 36], [364, 36], [198, 142], [364, 142]],
  windows: [[186, 70], [376, 70], [100, 24]],
  balcony: [[280, 178], [364, 264]],
  floors: [[138, 152], [244, 264]],
};

const Zone = ({ z, active, present }) => {
  if (!present) return null;
  return (
    <g>
      <rect x={z.x} y={z.y} width={z.w} height={z.h}
        fill={active ? WASH : "transparent"}
        stroke={active ? LINE_ACTIVE : LINE}
        strokeWidth={active ? 1.4 : 1} shapeRendering="crispEdges" />
      <text x={z.x + 8} y={z.y + 18} fill={active ? GOLD + "0.9)" : GOLD + "0.5)"}
        fontSize="9" letterSpacing="1.5" style={{ textTransform: "uppercase" }}>{z.label}</text>
    </g>
  );
};

export function PropertyBlueprint({ areas = [], activeArea, showMicro = false, hasInput = true }) {
  const reduce = useReducedMotion();
  const has = (id) => areas.includes(id);
  const micro = [];
  if (showMicro) {
    (activeArea ? [activeArea] : areas).forEach((id) => {
      (HOTSPOTS[id] || []).forEach((p, i) => micro.push({ id: `${id}-${i}`, x: p[0], y: p[1] }));
    });
  }

  return (
    <div className="relative w-full" data-testid="property-blueprint">
      <svg viewBox="0 0 400 300" className="w-full h-auto" role="img" aria-label="Architectural preview of your property profile">
        <defs>
          <pattern id="pi-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M20 0 L0 0 0 20" fill="none" stroke="rgba(255,255,255,0.035)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="400" height="300" fill="url(#pi-grid)" />

        {/* Outer shell */}
        <motion.rect
          x="12" y="12" width="376" height="276"
          fill="none" stroke={LINE_ACTIVE} strokeWidth="1.25" shapeRendering="crispEdges"
          initial={reduce ? false : { pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: reduce ? 0 : 0.9, ease: "easeOut" }}
        />

        {!hasInput ? (
          <text x="200" y="150" textAnchor="middle" fill={GOLD + "0.5)"} fontSize="11" letterSpacing="1">
            Begin to generate your Property Profile
          </text>
        ) : (
          <>
            <Zone z={ZONES.kitchen} present={has("kitchen")} active={activeArea === "kitchen"} />
            <Zone z={ZONES.bathrooms} present={has("bathrooms")} active={activeArea === "bathrooms"} />
            <Zone z={ZONES.bedrooms} present={has("bedrooms")} active={activeArea === "bedrooms"} />
            <Zone z={ZONES.floors} present={has("floors")} active={activeArea === "floors" || activeArea === "windows"} />
            <Zone z={ZONES.balcony} present={has("balcony")} active={activeArea === "balcony"} />

            {/* Window ticks along the outer shell (represent windows area) */}
            {has("windows") && [70, 150, 230].map((y) => (
              <line key={`wl-${y}`} x1="12" y1={y} x2="12" y2={y + 26}
                stroke={activeArea === "windows" ? LINE_ACTIVE : LINE} strokeWidth={activeArea === "windows" ? 3 : 2} />
            ))}
            {has("windows") && [120, 240].map((x) => (
              <line key={`wt-${x}`} x1={x} y1="12" x2={x + 30} y2="12"
                stroke={activeArea === "windows" ? LINE_ACTIVE : LINE} strokeWidth={activeArea === "windows" ? 3 : 2} />
            ))}

            {/* Micro-area hotspots */}
            {micro.map((m) => (
              <motion.g key={m.id}
                initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: reduce ? 0 : 0.2 }}>
                <rect x={m.x - 2.5} y={m.y - 2.5} width="5" height="5" fill={GOLD + "0.85)"} />
                <rect x={m.x - 5} y={m.y - 5} width="10" height="10" fill="none" stroke={GOLD + "0.4)"} strokeWidth="0.75" />
              </motion.g>
            ))}
          </>
        )}
      </svg>
    </div>
  );
}
